/**
 * useSeo — SPA 遷移に追従してルート別の <head> を書き換える。
 *
 * 静的HTML (scripts/generate-seo.js が build 後に生成) はクローラ向けの
 * 初期表示を担い、このフックは「アプリ内で遷移したあと」の title/description/
 * canonical/OGP を合わせる役割。両者は同じ src/seo/seoConfig.json を読む。
 */
import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import seoConfig from './seoConfig.json';
import comprehensiveTypes from '../data/comprehensive_types.json';

const setMeta = (selector, attr, name, content) => {
  if (!content) return;
  let el = document.head.querySelector(selector);
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(attr, name);
    document.head.appendChild(el);
  }
  el.setAttribute('content', content);
};

const setLink = (rel, href) => {
  let el = document.head.querySelector(`link[rel="${rel}"]`);
  if (!el) {
    el = document.createElement('link');
    el.setAttribute('rel', rel);
    document.head.appendChild(el);
  }
  el.setAttribute('href', href);
};

const removeMeta = (selector) => {
  const el = document.head.querySelector(selector);
  if (el) el.remove();
};

/** pathname から、そのページの title / description / noindex を決める */
export const resolveSeo = (pathname) => {
  const path = pathname.length > 1 ? pathname.replace(/\/+$/, '') : pathname;

  const exact = seoConfig.routes.find((r) => r.path === path);
  if (exact) {
    return { title: exact.title, description: exact.description, noindex: false };
  }

  if (path.startsWith(seoConfig.typePathPrefix)) {
    const code = path.slice(seoConfig.typePathPrefix.length).toUpperCase();
    const type = comprehensiveTypes[code];
    if (type) {
      return {
        title: `${type.name}（${code}）${type.archetype ? ' — ' + type.archetype : ''}｜STRUCT CODE`,
        description: (type.tagline || type.description || '').slice(0, 120),
        noindex: false,
      };
    }
  }

  const noindex = seoConfig.noindexPrefixes.some(
    (p) => path === p || path.startsWith(`${p}/`)
  );
  return {
    title: seoConfig.defaultTitle,
    description: seoConfig.defaultDescription,
    noindex,
  };
};

export default function useSeo() {
  const { pathname } = useLocation();

  useEffect(() => {
    const { title, description, noindex } = resolveSeo(pathname);
    const url = `${seoConfig.siteUrl}${pathname === '/' ? '/' : pathname}`;
    const image = `${seoConfig.siteUrl}${seoConfig.ogImage}`;

    document.title = title;
    setMeta('meta[name="description"]', 'name', 'description', description);
    setMeta('meta[property="og:title"]', 'property', 'og:title', title);
    setMeta('meta[property="og:description"]', 'property', 'og:description', description);
    setMeta('meta[property="og:url"]', 'property', 'og:url', url);
    setMeta('meta[property="og:image"]', 'property', 'og:image', image);
    setMeta('meta[name="twitter:title"]', 'name', 'twitter:title', title);
    setMeta('meta[name="twitter:description"]', 'name', 'twitter:description', description);
    setLink('canonical', url);

    // 会員向け・個人の結果ページなどはインデックスさせない
    if (noindex) {
      setMeta('meta[name="robots"]', 'name', 'robots', 'noindex, nofollow');
    } else {
      removeMeta('meta[name="robots"]');
    }
  }, [pathname]);
}
